import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ArrowLeft, Save, Loader } from 'lucide-react';

import StyleEditor from '../components/StyleEditor';
import PanfletoStyleEditor from '../components/PanfletoStyleEditor';
import StylePreview from '../components/StylePreview';
import { useLocation } from 'react-router-dom';
import { ContainerStyle, LayoutStyle, TextStyle, Screen, LayoutType, PriceBoxStyle } from '../../../shared/types';
import { useStore } from '../../../store/useStore';
import ImportExportButtons from '../../promotions/components/ImportExportButtons';
import { bannerSyncService, apiTelaToLocalScreen, TIPO_TO_LAYOUT, LAYOUT_TO_TIPO, jsonToLayoutStyle, extrairDescricaoDoJson, extrairNumeroColunasDoJson, buildDefaultLayoutStyleForLayout, mergeLayoutStyleForStyleSave } from '../../banners/services/bannerSyncService';
import {
  clonePanfletoContentDeep,
  defaultPanfletoContent,
  mergePanfletoContentPreserving,
} from '../../banners/utils/panfletoContentUtils';
import {
  buildPanfletoStyleExportPayload,
  isPanfletoStyleExportFile,
  mergePanfletoStyleImportIntoLayout,
} from '../utils/panfletoStyleImportExport';
import { bannerService } from '../../banners/services/bannerService';
import { useBannersParaStyleEditor } from '../../banners/hooks/useBanners';

interface LocationState {
  screenId?: number;
}

// Default text style
const defaultTextStyle: TextStyle = {
  color: '#000000',
  fontSize: 16,
  fontFamily: 'Arial, sans-serif',
};

// Default container style
const defaultContainerStyle: ContainerStyle = {
  backgroundColor: '#ffffff',
  useGradient: false,
  gradientStart: '#ffffff',
  gradientEnd: '#f0f0f0',
  borderRadius: 8,
  paddingTop: 16,
  paddingBottom: 16,
  paddingLeft: 16,
  paddingRight: 16,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  backgroundVignetteEnabled: false,
  backgroundVignetteIntensity: 35,
};

// Default layout style
const defaultLayoutStyle: LayoutStyle = {
  productName: { ...defaultTextStyle },
  price: { ...defaultTextStyle },
  unit: { ...defaultTextStyle },
  productContainer: { ...defaultContainerStyle },
  priceBox: {
    enabled: false,
    backgroundColor: '#ffffff',
    borderColor: '#000000',
    borderWidth: 0,
    borderRadius: 8,
    paddingTop: 4,
    paddingRight: 8,
    paddingBottom: 4,
    paddingLeft: 8,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
  } as PriceBoxStyle,
};

// Padrão visual por tipo de tela (fonte única em bannerSyncService)
const getDefaultLayoutStyleForLayoutId = (layoutId: LayoutType): LayoutStyle =>
  buildDefaultLayoutStyleForLayout(layoutId);

const isIsoDateRangeInvalid = (start?: string, end?: string): boolean => {
  if (!start || !end) return false;
  return String(start) > String(end);
};

const StyleEditorPage: React.FC = () => {
  const location = useLocation();
  const { screens: screensStore, updateScreen } = useStore();
  // Listagem via `listarTelasOtimizado`: configuracoes + produtoIds (certeza se a tela tem produto).
  const { telas: telasOtimizadas, loading: loadingTelasAPI, error: errorTelasAPI } = useBannersParaStyleEditor();
  const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [screenFromAPI, setScreenFromAPI] = useState<Screen | null>(null);
  // Estado local para edição em tempo real (não salva no store até clicar em Salvar)
  const [currentLayoutStyle, setCurrentLayoutStyle] = useState<LayoutStyle | null>(null);
  // Cache local para configurações atualizadas durante a edição (não salvas na API ainda ou já salvas mas não recarregadas)
  // IMPORTANTE: Este cache permite que ao alternar entre telas, as configurações salvas sejam mostradas imediatamente
  // Cache: telaId -> LayoutStyle
  const configuracoesUpdatedCache = useRef<Map<number, LayoutStyle>>(new Map());
  // Estado para forçar recálculo do useMemo quando o cache mudar
  const [cacheVersion, setCacheVersion] = useState(0);
  // Ref para obter o estado atual do StyleEditor (para exportação completa)
  const getCurrentStyleFromEditor = useRef<(() => LayoutStyle) | null>(null);
  // Estado para forçar atualização do StyleEditor quando importar
  const [importKey, setImportKey] = useState(0);
  const [numeroColunas, setNumeroColunas] = useState<number | undefined>(undefined);
  const panfletoFotoRemotaRef = useRef<Map<number, boolean>>(new Map());
  /** Evita que `buscarFotoTela` (assíncrono) sobrescreva foto local ou remoção feita antes do fetch terminar. */
  const panfletoBgUserModifiedRef = useRef(false);
  const [panfletoFotoLoading, setPanfletoFotoLoading] = useState(false);

    // Converter telas da API (`TelaBannerOtimizada`: produtoIds + configuracoes) para Screen
    // IMPORTANTE: Filtrar telas do tipo "video" (tipo 2) - elas não devem aparecer no editor de estilos
    const screensFromAPI = useMemo(() => {
      if (!telasOtimizadas || telasOtimizadas.length === 0) {
        return [];
      }

      // Filtrar telas do tipo "video" (tipo 2) - elas não devem aparecer no editor de estilos
      return telasOtimizadas
        .filter(telaOtimizada => telaOtimizada.tipo !== 2)
        .map(telaOtimizada => {
          // Converter tipo para layoutId
          const layoutId = telaOtimizada.tipo !== undefined
            ? (TIPO_TO_LAYOUT[telaOtimizada.tipo] || 'simple2col') as LayoutType
            : 'simple2col' as LayoutType;

          // Buscar dados complementares do store local se existir
          const screenStore = screensStore.find(s => s.id === telaOtimizada.id);

          // Converter configuracoes (JSON string) para layoutStyle se existir
          // PRIORIDADE: Cache local (configurações atualizadas) > API (configuracoes)
          let layoutStyle: LayoutStyle | undefined = undefined;

          // Verificar primeiro se há configuração atualizada no cache local
          const cachedLayoutStyle = configuracoesUpdatedCache.current.get(telaOtimizada.id);
          if (cachedLayoutStyle) {
            layoutStyle = cachedLayoutStyle;
          } else if (telaOtimizada.configuracoes) {
            // Se não há no cache, usar a função de conversão do bannerSyncService
            layoutStyle = jsonToLayoutStyle(telaOtimizada.configuracoes);
          }

          const apiProdutoIds = Array.isArray(telaOtimizada.produtoIds) ? telaOtimizada.produtoIds : [];
          // Lista otimizada da API é fonte de verdade: [] após remover todos os produtos (não cair no store antigo).
          const promotionsFromApi = apiProdutoIds;

          return {
            id: telaOtimizada.id,
            name: telaOtimizada.nome,
            description: String(telaOtimizada.descricao || '').trim() || screenStore?.description || '',
            layoutId: layoutId,
            promotions: promotionsFromApi,
            isActive: telaOtimizada.ativo ?? screenStore?.isActive ?? false,
            backgroundColor: screenStore?.backgroundColor || '#ffffff',
            backgroundImage: screenStore?.backgroundImage,
            showHeader: screenStore?.showHeader ?? true,
            duration: screenStore?.duration || 30,
            // IMPORTANTE: layoutStyle agora vem da API (via configuracoes), não do store local
            layoutStyle: layoutStyle,
            createdAt: screenStore?.createdAt || new Date().toISOString(),
            linkedProducts:
              apiProdutoIds.length > 0 ? screenStore?.linkedProducts : []
          } as Screen;
        });
  }, [telasOtimizadas, screensStore, cacheVersion]); // Incluir cacheVersion para forçar recálculo quando cache mudar

  // Usar telas da API se disponíveis, senão usar do store local como fallback
  // IMPORTANTE: Filtrar telas do tipo "video" também do store local
  const screens = screensFromAPI.length > 0
    ? screensFromAPI
    : screensStore.filter(s => s.layoutId !== 'video');

  useEffect(() => {
    // Get screenId from location state if available
    const state = location.state as LocationState;
    if (state && state.screenId) {
      setSelectedScreenId(state.screenId);
    }
  }, [location]);

  // Cache para telas já carregadas com suas configurações completas (usando useRef para evitar re-renders)
  // IMPORTANTE: Este cache armazena telas que já foram buscadas da API com suas configurações (layoutStyle)
  const screenConfigCache = useRef<Map<number, Screen>>(new Map());

  // Quando uma tela for selecionada, usar dados já carregados ou buscar da API
  // ESTRATÉGIA DE OTIMIZAÇÃO (em ordem de prioridade):
  // 1. Cache (já processou antes nesta sessão) → instantâneo
  // 2. Dados da listagem (`listarTelasOtimizado` no hook) → configuracoes + produtoIds
  // 3. Buscar da API (só se a tela não estiver na listagem) → caso raro
  // IMPORTANTE: As configurações (layoutStyle) vêm da API (configuracoes) ou cache local de edição
  useEffect(() => {
    const loadScreenFromAPI = async () => {
      if (!selectedScreenId || selectedScreenId <= 0) {
        setScreenFromAPI(null);
        setCurrentLayoutStyle(null);
        setNumeroColunas(undefined);
        return;
      }

      // PRIORIDADE 1: Verificar cache de configurações atualizadas (configurações salvas durante a edição)
      const cachedUpdatedLayoutStyle = configuracoesUpdatedCache.current.get(selectedScreenId);

      // PRIORIDADE 2: Verificar cache de telas (já processou antes nesta sessão)
      const cachedScreen = screenConfigCache.current.get(selectedScreenId);
      if (cachedScreen) {
        // Usar dados do cache - instantâneo!
        // Se há configuração atualizada no cache, usar ela (prioridade sobre cache de tela)
        const layoutStyleToUse = cachedUpdatedLayoutStyle || cachedScreen.layoutStyle || getDefaultLayoutStyleForLayoutId(cachedScreen.layoutId || 'simple2col');
        const telaNaLista = telasOtimizadas.find((t) => t.id === selectedScreenId);
        const idsLista = Array.isArray(telaNaLista?.produtoIds) ? telaNaLista!.produtoIds! : [];
        const screenToUse: Screen = {
          ...cachedScreen,
          layoutStyle: layoutStyleToUse,
          promotions: telaNaLista !== undefined ? idsLista : cachedScreen.promotions || [],
          linkedProducts:
            telaNaLista !== undefined
              ? idsLista.length > 0
                ? cachedScreen.linkedProducts
                : []
              : cachedScreen.linkedProducts,
        };
        screenConfigCache.current.set(selectedScreenId, screenToUse);
        setScreenFromAPI(screenToUse);
        setCurrentLayoutStyle(layoutStyleToUse);
        
        // IMPORTANTE: Extrair numeroColunas do JSON de configurações (buscar da telasOtimizadas)
        const telaOtimizada = telasOtimizadas.find(t => t.id === selectedScreenId);
        if (telaOtimizada && telaOtimizada.configuracoes) {
          const numeroColunasSalvo = extrairNumeroColunasDoJson(telaOtimizada.configuracoes);
          const numeroColunasAtual = (cachedScreen.layoutId === 'simple2col' && numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3) 
            ? numeroColunasSalvo 
            : (cachedScreen.layoutId === 'simple2col' ? 2 : undefined);
          setNumeroColunas(numeroColunasAtual);
        } else {
          // Se não encontrar, usar padrão baseado no layoutId
          const numeroColunasAtual = cachedScreen.layoutId === 'simple2col' ? 2 : undefined;
          setNumeroColunas(numeroColunasAtual);
        }
        return;
      }

      // PRIORIDADE 3: Usar dados já carregados do useBannersOtimizado (sem nova busca à API!)
      // IMPORTANTE: Agora useBannersOtimizado() já retorna configuracoes, então podemos usar diretamente
      const existingScreen = screens.find(s => s.id === selectedScreenId);
      if (existingScreen) {
        // Temos a tela já carregada com configuracoes da API - usar diretamente!
        // Se há configuração atualizada no cache, usar ela (prioridade sobre dados da API)
        const layoutStyleToUse = cachedUpdatedLayoutStyle || existingScreen.layoutStyle || getDefaultLayoutStyleForLayoutId(existingScreen.layoutId || 'simple2col');
        const telaNaLista = telasOtimizadas.find((t) => t.id === selectedScreenId);
        const idsLista = Array.isArray(telaNaLista?.produtoIds) ? telaNaLista!.produtoIds! : [];
        const screenToUse: Screen = {
          ...existingScreen,
          layoutStyle: layoutStyleToUse,
          promotions: telaNaLista !== undefined ? idsLista : existingScreen.promotions || [],
          linkedProducts:
            telaNaLista !== undefined
              ? idsLista.length > 0
                ? existingScreen.linkedProducts
                : []
              : existingScreen.linkedProducts,
        };
        setScreenFromAPI(screenToUse);
        setCurrentLayoutStyle(layoutStyleToUse);
        
        // IMPORTANTE: Extrair numeroColunas do JSON de configurações
        const telaOtimizada = telasOtimizadas.find(t => t.id === selectedScreenId);
        if (telaOtimizada && telaOtimizada.configuracoes) {
          const numeroColunasSalvo = extrairNumeroColunasDoJson(telaOtimizada.configuracoes);
          const numeroColunasAtual = (existingScreen.layoutId === 'simple2col' && numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3) 
            ? numeroColunasSalvo 
            : (existingScreen.layoutId === 'simple2col' ? 2 : undefined);
          setNumeroColunas(numeroColunasAtual);
        } else {
          // Se não encontrar, usar padrão baseado no layoutId
          const numeroColunasAtual = existingScreen.layoutId === 'simple2col' ? 2 : undefined;
          setNumeroColunas(numeroColunasAtual);
        }
        
        // Cachear para próximas vezes (inclui promotions preenchidos pelo enrich)
        screenConfigCache.current.set(selectedScreenId, screenToUse);
        return;
      }

      // PRIORIDADE 3: Buscar da API (só se não tiver nos dados já carregados)
      // Isso só acontece se a tela não foi carregada no useBannersOtimizado (caso raro)
      setLoadingScreen(true);
      try {
        // IMPORTANTE: Usar listarTelasOtimizado para garantir que temos TODAS as informações
        // (produtos, configurações, etc) sempre alinhadas com a API
        const telasAPI = await bannerService.listarTelasOtimizado(selectedScreenId);
        const telaAPI = telasAPI.find(t => t.id === selectedScreenId);

        if (telaAPI) {
          // IMPORTANTE: TelaBannerOtimizada já tem produtoIds como number[] e configuracoes como string
          // Converter diretamente para Screen, garantindo que temos TODAS as informações da API
          const layoutId = telaAPI.tipo !== undefined
            ? (TIPO_TO_LAYOUT[telaAPI.tipo] || 'simple2col') as LayoutType
            : 'simple2col' as LayoutType;

          // Converter configuracoes (JSON string) para layoutStyle
          const layoutStyleFromAPI = jsonToLayoutStyle(telaAPI.configuracoes);
          
          // IMPORTANTE: Extrair número de colunas do JSON de configurações (apenas para tipo 0 - simple2col)
          const numeroColunasSalvo = extrairNumeroColunasDoJson(telaAPI.configuracoes);
          const numeroColunasAtual = (layoutId === 'simple2col' && numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3) 
            ? numeroColunasSalvo 
            : (layoutId === 'simple2col' ? 2 : undefined); // Padrão: 2 colunas para simple2col
          setNumeroColunas(numeroColunasAtual);

          // Buscar a tela local apenas para pegar dados complementares (linkedProducts, createdAt, etc)
          const localScreenData = screens.find(s => s.id === selectedScreenId);

          // IMPORTANTE: Verificar cache de configurações atualizadas primeiro (prioridade sobre API)
          const cachedUpdatedLayoutStyle = configuracoesUpdatedCache.current.get(selectedScreenId);

          // Prioridade: Cache atualizado > API > Default
          const layoutStyleToUse = cachedUpdatedLayoutStyle || layoutStyleFromAPI || getDefaultLayoutStyleForLayoutId(layoutId);

          // IMPORTANTE: Extrair descrição do JSON de configurações se não estiver no campo descricao
          let description = telaAPI.descricao || localScreenData?.description || '';
          if (!description && telaAPI.configuracoes) {
            const descricaoDoJson = extrairDescricaoDoJson(telaAPI.configuracoes);
            if (descricaoDoJson) {
              description = descricaoDoJson;
            }
          }

          // Combinar dados - IMPORTANTE: Usar dados da API (sempre atualizados) como base
          const combinedScreen: Screen = {
            id: selectedScreenId,
            name: telaAPI.nome || localScreenData?.name || 'Tela sem nome',
            description: description,
            layoutId: layoutId,
            promotions: telaAPI.produtoIds || [], // IMPORTANTE: Usar produtoIds da API (sempre atualizados)
            linkedProducts:
              (telaAPI.produtoIds || []).length > 0 ? localScreenData?.linkedProducts || [] : [],
            isActive: telaAPI.ativo ?? localScreenData?.isActive ?? false,
            createdAt: localScreenData?.createdAt || new Date().toISOString(),
            backgroundColor: localScreenData?.backgroundColor || '#ffffff',
            backgroundImage: localScreenData?.backgroundImage,
            showHeader: localScreenData?.showHeader ?? true,
            duration: localScreenData?.duration || 30,
            layoutStyle: layoutStyleToUse
          };

          setScreenFromAPI(combinedScreen);
          setCurrentLayoutStyle(layoutStyleToUse);

          // Cachear para próximas vezes
          screenConfigCache.current.set(selectedScreenId, combinedScreen);
        } else {
          setScreenFromAPI(null);
          setCurrentLayoutStyle(null);
          setNumeroColunas(undefined);
        }
      } catch (error) {
        setScreenFromAPI(null);
        setCurrentLayoutStyle(null);
        setNumeroColunas(undefined);
      } finally {
        setLoadingScreen(false);
      }
    };

    loadScreenFromAPI();
    // IMPORTANTE: Remover screens e screenConfigCache das dependências para evitar loop
    // screenConfigCache é atualizado dentro do effect, então não precisa estar nas dependências
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScreenId]);

  // Usar tela da API se disponível, senão usar do store local como fallback temporário
  // IMPORTANTE: 
  // - screenFromAPI sempre tem layoutStyle da API (ou default) - SEMPRE priorizado
  // - O fallback para screens (store local) só acontece durante loading ou se a API falhar
  // - Se o fallback usar layoutStyle do store local, será temporário até a API responder
  // - O cache garante que na próxima seleção não precisamos esperar a API
  const selectedScreen = screenFromAPI || (selectedScreenId
    ? screens.find(s => s.id === selectedScreenId) || null
    : null);

  // Garantir objeto panfletoContent ao editar telas panfleto (JSON antigo sem a chave)
  useEffect(() => {
    if (selectedScreen?.layoutId !== 'panfleto' || !currentLayoutStyle) return;
    if (currentLayoutStyle.panfletoContent !== undefined) return;
    setCurrentLayoutStyle({
      ...currentLayoutStyle,
      panfletoContent: defaultPanfletoContent(),
    });
  }, [selectedScreen?.id, selectedScreen?.layoutId, currentLayoutStyle?.panfletoContent]);

  // Panfleto: carrega foto de fundo no endpoint dedicado da tela (fora do ds_configuracoes).
  useEffect(() => {
    const screenId = selectedScreen?.id;
    if (!screenId || selectedScreen?.layoutId !== 'panfleto') {
      setPanfletoFotoLoading(false);
      return;
    }
    panfletoBgUserModifiedRef.current = false;
    let cancelled = false;
    setPanfletoFotoLoading(true);
    (async () => {
      try {
        const foto = await bannerService.buscarFotoTela(screenId);
        if (cancelled) return;
        panfletoFotoRemotaRef.current.set(screenId, Boolean(foto));
        setCurrentLayoutStyle(prev => {
          if (!prev) return prev;
          if (panfletoBgUserModifiedRef.current) return prev;
          const cur = prev.productContainer?.backgroundImage;
          if (typeof cur === 'string' && (cur.startsWith('data:') || cur.startsWith('blob:'))) {
            return prev;
          }
          return {
            ...prev,
            productContainer: {
              ...prev.productContainer,
              backgroundImage: foto ?? undefined,
            },
          };
        });
      } finally {
        if (!cancelled) setPanfletoFotoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setPanfletoFotoLoading(false);
    };
  }, [selectedScreen?.id, selectedScreen?.layoutId]);

  // Garantir que currentLayoutStyle seja atualizado quando selectedScreen mudar
  // Este useEffect é um fallback caso o useEffect principal não defina o currentLayoutStyle
  // IMPORTANTE: Este fallback só é executado se screenFromAPI não foi definido ainda (loading ou erro)
  // Se screenFromAPI existe, ele sempre tem layoutStyle da API, então este fallback não é necessário
  useEffect(() => {
    if (selectedScreen && !currentLayoutStyle) {
      // NOTA: Se selectedScreen vem de screenFromAPI, layoutStyle já vem da API
      // Se vem de screens (store local), pode ter layoutStyle desatualizado, mas é apenas um fallback
      // O ideal é que screenFromAPI sempre seja definido antes deste fallback executar
      if (selectedScreen.layoutStyle) {
        setCurrentLayoutStyle(selectedScreen.layoutStyle);
      } else {
        // Se não houver layoutStyle na tela, usar default baseado no layoutId
        const defaultStyle = getDefaultLayoutStyleForLayoutId(selectedScreen.layoutId || 'simple2col');
        setCurrentLayoutStyle(defaultStyle);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScreen?.id]);

  // Produto de teste padrão para preview (sempre usar este produto, sem fazer requests na API)
  const previewPromotion = {
    id: 0,
    name: 'Produto de Exemplo',
    price: 29.99,
    originalPrice: 39.99,
    unit: 'UN',
    image: undefined,
    video: undefined,
    validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    description: 'Este é um produto de exemplo para visualização do estilo',
    category: 'Categoria Exemplo',
    isVideoPromotion: false,
    hasAudio: false,
    autoplay: false,
    loop: false,
    createdAt: new Date().toISOString()
  };

  // Função helper para criar cópia profunda SEGURA que preserva TODOS os dados
  // CRÍTICO: Esta função garante que NENHUM dado seja perdido, especialmente imagens base64
  const deepCopyLayoutStyle = (style: LayoutStyle): LayoutStyle => {
    // Usar spread operator e cópia manual para garantir que NADA seja perdido
    return {
      productName: style.productName ? { ...style.productName } : undefined,
      price: style.price ? { ...style.price } : undefined,
      unit: style.unit ? { ...style.unit } : undefined,
      productContainer: style.productContainer ? {
        ...style.productContainer,
        // CRÍTICO: Preservar backgroundImage exatamente como está (pode ser base64 muito grande)
        backgroundImage: style.productContainer.backgroundImage,
      } : undefined,
      description: style.description ? { ...style.description } : undefined,
      category: style.category ? { ...style.category } : undefined,
      validUntil: style.validUntil ? { ...style.validUntil } : undefined,
      priceBox: style.priceBox ? { ...style.priceBox } : undefined,
      panfletoContent: style.panfletoContent ? clonePanfletoContentDeep(style.panfletoContent) : undefined,
    } as LayoutStyle;
  };

  const handleStyleChange = (newStyle: LayoutStyle) => {
    // Atualizar apenas o estado local para preview em tempo real
    // NÃO atualizar o store até o usuário clicar em "Salvar"
    setCurrentLayoutStyle(newStyle);
  };

  // Memoizar callback para evitar re-renders desnecessários dos sliders
  const handleGetCurrentStyle = useCallback((getStyle: () => LayoutStyle) => {
    getCurrentStyleFromEditor.current = getStyle;
  }, []);

  const handleSaveToAPI = async () => {
    if (!selectedScreen) {
      setSaveMessage({ type: 'error', text: 'Nenhuma tela selecionada' });
      return;
    }

    // Verificar se a tela tem um ID válido (deve ser um ID da API)
    if (!selectedScreen.id || selectedScreen.id <= 0) {
      setSaveMessage({ type: 'error', text: 'A tela precisa ser salva na API primeiro antes de salvar as configurações' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      // IMPORTANTE: Obter o estado MAIS ATUALIZADO do editor
      // Prioridade: estado interno do StyleEditor > currentLayoutStyle > selectedScreen.layoutStyle
      // CRÍTICO: Criar cópia profunda SEGURA que preserve TODOS os dados, especialmente imagens base64
      let layoutStyleParaSalvar: LayoutStyle | null = null;

      // CRÍTICO: Sempre usar currentLayoutStyle como fonte principal
      // O currentLayoutStyle é atualizado em tempo real pelo handleStyleChange
      // e sempre reflete as alterações mais recentes do usuário
      if (currentLayoutStyle) {
        // Criar cópia profunda SEGURA que preserva TODOS os dados
        layoutStyleParaSalvar = deepCopyLayoutStyle(currentLayoutStyle);
      }

      // Fallback: tentar obter do estado interno do editor se currentLayoutStyle não estiver disponível
      if (!layoutStyleParaSalvar && getCurrentStyleFromEditor.current) {
        try {
          const styleFromEditor = getCurrentStyleFromEditor.current();
          // Criar cópia profunda SEGURA que preserva TODOS os dados
          if (styleFromEditor) {
            layoutStyleParaSalvar = deepCopyLayoutStyle(styleFromEditor);
          }
        } catch (error) {
          console.warn('Erro ao obter estilo do editor:', error);
        }
      }

      // Fallback final: usar do selectedScreen se ainda não tiver
      if (!layoutStyleParaSalvar && selectedScreen.layoutStyle) {
        // Criar cópia profunda SEGURA que preserva TODOS os dados
        layoutStyleParaSalvar = deepCopyLayoutStyle(selectedScreen.layoutStyle);
      }

      if (!layoutStyleParaSalvar) {
        setSaveMessage({ type: 'error', text: 'Nenhuma configuração para salvar' });
        setSaving(false);
        return;
      }

      // Panfleto: validar intervalo de datas digitadas antes de persistir no JSON.
      if (selectedScreen.layoutId === 'panfleto') {
        const dataInicial = layoutStyleParaSalvar.panfletoContent?.validadeDataInicial;
        const dataFinal = layoutStyleParaSalvar.panfletoContent?.validadeDataFinal;
        if (isIsoDateRangeInvalid(dataInicial, dataFinal)) {
          setSaveMessage({
            type: 'error',
            text: 'Não foi possivel salvar: a data inicial nao pode ser maior que a data final.',
          });
          setSaving(false);
          return;
        }
      }

      // Panfleto: foto de fundo em multipart POST/PUT /v1/banner/tela/{id}/foto (campo Foto), nunca em ds_configuracoes.
      if (selectedScreen.layoutId === 'panfleto') {
        const bgDataUrl = layoutStyleParaSalvar.productContainer?.backgroundImage;
        const bgRaw = String(bgDataUrl || '').trim();
        const hadRemote = panfletoFotoRemotaRef.current.get(selectedScreen.id) === true;
        const isBlobUrl = bgRaw.startsWith('blob:');
        if (bgRaw && !isBlobUrl) {
          const fotoBase64 = bgRaw.startsWith('data:') ? (bgRaw.split(',')[1] || '') : bgRaw;
          if (fotoBase64) {
            await bannerService.salvarFotoTela(selectedScreen.id, fotoBase64, hadRemote);
            panfletoFotoRemotaRef.current.set(selectedScreen.id, true);
          }
        } else if (!bgRaw && hadRemote) {
          await bannerService.deletarFotoTela(selectedScreen.id);
          panfletoFotoRemotaRef.current.set(selectedScreen.id, false);
        }
        layoutStyleParaSalvar = {
          ...layoutStyleParaSalvar,
          productContainer: {
            ...layoutStyleParaSalvar.productContainer,
            backgroundImage: undefined,
          },
        };
        // Recarregar imediatamente a foto do endpoint para manter preview/display coerentes.
        const fotoAtual = await bannerService.buscarFotoTela(selectedScreen.id);
        panfletoBgUserModifiedRef.current = false;
        setCurrentLayoutStyle(prev =>
          prev
            ? {
                ...prev,
                productContainer: {
                  ...prev.productContainer,
                  backgroundImage:
                    fotoAtual ??
                    (bgRaw && bgRaw.startsWith('data:') ? bgRaw : prev.productContainer?.backgroundImage),
                },
              }
            : prev
        );
      }

      // IMPORTANTE: Quando for 3 colunas, forçar marginRight e marginLeft para 0 antes de salvar
      if (numeroColunas === 3 && layoutStyleParaSalvar.productContainer) {
        layoutStyleParaSalvar = {
          ...layoutStyleParaSalvar,
          productContainer: {
            ...layoutStyleParaSalvar.productContainer,
            marginRight: 0,
            marginLeft: 0
          }
        };
      }

      // JSON fresco da API antes do PUT: margens externas e panfleto alinhados ao que já está salvo.
      const telasFresh = await bannerService.listarTelasOtimizado(selectedScreen.id);
      const telaFresh = telasFresh.find((t) => t.id === selectedScreen.id);
      const freshStyle = jsonToLayoutStyle(telaFresh?.configuracoes);
      if (freshStyle) {
        layoutStyleParaSalvar = mergeLayoutStyleForStyleSave(freshStyle, layoutStyleParaSalvar);
      }

      // IMPORTANTE: A API exige o campo nome e tipo no PUT
      // Enviar apenas: id, nome, tipo e configurações (SEM produtos)
      const telaAtual = telasOtimizadas.find(t => t.id === selectedScreen.id);
      const tipoTela = telaAtual?.tipo ?? 0;
      // O tipo será calculado no bannerSyncService a partir do layoutId
      const tipoCalculado = selectedScreen.layoutId ? (LAYOUT_TO_TIPO[selectedScreen.layoutId] ?? tipoTela) : tipoTela;

      const screenToSave: Partial<Screen> = {
        name: selectedScreen.name, // Nome obrigatório pela API
        layoutId: selectedScreen.layoutId, // Para converter para tipo no bannerSyncService
        layoutStyle: layoutStyleParaSalvar // Configurações sendo alteradas (sempre enviar as mais atualizadas)
        // NÃO incluir promotions - isso faz com que produtos NÃO sejam enviados
      };

      // Atualizar a tela na API com configurações + nome + tipo (SEM produtos)
      console.log(`[StyleEditor] Salvando estilo da tela ${selectedScreen.id}...`);
      const success = await bannerSyncService.atualizarTelaAPI(selectedScreen.id, screenToSave);

      if (success) {
        console.log(`[StyleEditor] ✅ Estilo da tela ${selectedScreen.id} salvo com sucesso!`);
        console.log(`[StyleEditor] O backend deve notificar via SignalR automaticamente após o PUT`);
        setSaveMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });

        // IMPORTANTE: Recarregar dados COMPLETOS da API após salvar para garantir
        // que tudo está 100% alinhado (produtos, configurações, etc)
        // Isso garante que mesmo que não enviemos produtos ao salvar, temos todos os dados atualizados
        try {
          const telasRecarregadas = await bannerService.listarTelasOtimizado(selectedScreen.id);
          const telaRecarregada = telasRecarregadas.find(t => t.id === selectedScreen.id);

          if (telaRecarregada) {
            // Converter tela recarregada da API para formato Screen
            const layoutId = telaRecarregada.tipo !== undefined
              ? (TIPO_TO_LAYOUT[telaRecarregada.tipo] || 'simple2col') as LayoutType
              : 'simple2col' as LayoutType;

            // Converter configuracoes (JSON string) para layoutStyle
            const layoutStyleFromAPI = jsonToLayoutStyle(telaRecarregada.configuracoes);
            
            // IMPORTANTE: Extrair número de colunas do JSON de configurações (apenas para tipo 0 - simple2col)
            const numeroColunasSalvo = extrairNumeroColunasDoJson(telaRecarregada.configuracoes);
            const numeroColunasAtual = (layoutId === 'simple2col' && numeroColunasSalvo !== undefined && numeroColunasSalvo >= 1 && numeroColunasSalvo <= 3) 
              ? numeroColunasSalvo 
              : (layoutId === 'simple2col' ? 2 : undefined); // Padrão: 2 colunas para simple2col
            setNumeroColunas(numeroColunasAtual);

            // Buscar dados complementares do store local (linkedProducts, createdAt, etc)
            const localScreenData = screens.find(s => s.id === selectedScreen.id);

            // IMPORTANTE: Extrair descrição do JSON de configurações se não estiver no campo descricao
            let descriptionRecarregada = telaRecarregada.descricao || localScreenData?.description || '';
            if (!descriptionRecarregada && telaRecarregada.configuracoes) {
              const descricaoDoJson = extrairDescricaoDoJson(telaRecarregada.configuracoes);
              if (descricaoDoJson) {
                descriptionRecarregada = descricaoDoJson;
              }
            }

            // Construir Screen completo com TODOS os dados da API (sempre alinhado)
            const screenCompletoDaAPI: Screen = {
              id: selectedScreen.id,
              name: telaRecarregada.nome || localScreenData?.name || 'Tela sem nome',
              description: descriptionRecarregada,
              layoutId: layoutId,
              promotions: telaRecarregada.produtoIds || [], // IMPORTANTE: Produtos sempre da API
              linkedProducts:
                (telaRecarregada.produtoIds || []).length > 0 ? localScreenData?.linkedProducts || [] : [],
              isActive: telaRecarregada.ativo ?? localScreenData?.isActive ?? false,
              createdAt: localScreenData?.createdAt || new Date().toISOString(),
              backgroundColor: localScreenData?.backgroundColor || '#ffffff',
              backgroundImage: localScreenData?.backgroundImage,
              showHeader: localScreenData?.showHeader ?? true,
              duration: localScreenData?.duration || 30,
              layoutStyle: layoutStyleFromAPI || getDefaultLayoutStyleForLayoutId(layoutId) // Configurações sempre da API
            };

            // IMPORTANTE: Substituir completamente screenFromAPI com dados da API
            // Isso garante que temos TODAS as informações sempre alinhadas
            setScreenFromAPI(screenCompletoDaAPI);

            // IMPORTANTE: Atualizar currentLayoutStyle com o que veio da API
            // CRÍTICO: Usar cópia profunda SEGURA que preserva TODOS os dados
            if (layoutStyleFromAPI) {
              // Criar uma cópia profunda SEGURA que preserva TODOS os dados
              const newLayoutStyle = deepCopyLayoutStyle(layoutStyleFromAPI);
              if (layoutId === 'panfleto') {
                const fotoAtual = await bannerService.buscarFotoTela(selectedScreen.id);
                if (fotoAtual) {
                  newLayoutStyle.productContainer = {
                    ...newLayoutStyle.productContainer,
                    backgroundImage: fotoAtual,
                  };
                }
              }
              setCurrentLayoutStyle(newLayoutStyle);
            } else {
              // Se não houver layoutStyle da API, usar default
              const defaultStyle = getDefaultLayoutStyleForLayoutId(layoutId);
              setCurrentLayoutStyle(defaultStyle);
            }

            // IMPORTANTE: Atualizar cache de telas com dados COMPLETOS da API
            screenConfigCache.current.set(selectedScreen.id, screenCompletoDaAPI);

            // IMPORTANTE: Limpar cache de configurações atualizadas para esta tela
            // Já que agora temos os dados atualizados da API, não precisamos mais do cache local
            configuracoesUpdatedCache.current.delete(selectedScreen.id);

            // Forçar recálculo do useMemo que constrói screensFromAPI
            setCacheVersion(prev => prev + 1);

            // IMPORTANTE: Forçar sincronização do StyleEditor após salvar
            // Isso garante que o editor sincronize com os dados recarregados da API
            setImportKey(prev => prev + 1);
          }
        } catch (error) {
          console.error('Erro ao recarregar dados da API após salvar:', error);
          // Mesmo se falhar ao recarregar, atualizar com o que foi salvo localmente
          if (layoutStyleParaSalvar) {
            setCurrentLayoutStyle(layoutStyleParaSalvar);
          }
        }

        // Limpar mensagem após 3 segundos
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({ type: 'error', text: 'Erro ao salvar configurações' });
      }
    } catch (error) {
      setSaveMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Erro ao salvar configurações na API'
      });
    } finally {
      setSaving(false);
    }
  };

  const resolveStyleToExportFromSession = useCallback((): LayoutStyle | null => {
    if (!selectedScreen) return null;
    let styleToExport: LayoutStyle | null = null;
    if (getCurrentStyleFromEditor.current) {
      try {
        styleToExport = getCurrentStyleFromEditor.current();
      } catch (error) {
        console.warn('Erro ao obter estilo do editor:', error);
      }
    }
    if (!styleToExport) {
      styleToExport = currentLayoutStyle || selectedScreen.layoutStyle || null;
    }
    return styleToExport;
  }, [selectedScreen, currentLayoutStyle]);

  const handleExportImportStyle = () => {
    if (!selectedScreen) return null;

    const styleToExport = resolveStyleToExportFromSession();
    if (!styleToExport) return null;

    /** Panfleto: JSON dedicado (campos do cartaz + tipografia), lógica em `panfletoStyleImportExport`. */
    if (selectedScreen.layoutId === 'panfleto') {
      return buildPanfletoStyleExportPayload(styleToExport);
    }

    // Demais layouts: exportação completa de LayoutStyle (sem panfletoContent no JSON)
    // Criar uma cópia profunda para garantir que todos os campos sejam incluídos no JSON
    // Mesclar com baseStyle para garantir que campos que podem estar faltando sejam incluídos
    const baseStyle = selectedScreen.layoutStyle;

    // Construir objeto completo mesclando estilo atual com base (para garantir campos que podem estar faltando)
    // Garantir que campos obrigatórios sempre tenham valores
    const completeStyle: LayoutStyle = {
      productName: {
        ...(baseStyle?.productName || { color: '#000000', fontSize: 16 }),
        ...styleToExport.productName,
        // Garantir que campos obrigatórios sempre existam
        color: styleToExport.productName?.color || baseStyle?.productName?.color || '#000000',
        fontSize: styleToExport.productName?.fontSize || baseStyle?.productName?.fontSize || 16,
      },
      price: {
        ...(baseStyle?.price || { color: '#000000', fontSize: 16 }),
        ...styleToExport.price,
        // Garantir que campos obrigatórios sempre existam
        color: styleToExport.price?.color || baseStyle?.price?.color || '#000000',
        fontSize: styleToExport.price?.fontSize || baseStyle?.price?.fontSize || 16,
      },
      unit: {
        ...(baseStyle?.unit || { color: '#000000', fontSize: 16 }),
        ...styleToExport.unit,
        // Garantir que campos obrigatórios sempre existam
        color: styleToExport.unit?.color || baseStyle?.unit?.color || '#000000',
        fontSize: styleToExport.unit?.fontSize || baseStyle?.unit?.fontSize || 16,
      },
      productContainer: {
        ...(baseStyle?.productContainer || {}),
        ...styleToExport.productContainer,
      },
    };

    // Incluir campos opcionais se existirem em qualquer um dos estilos
    // Incluir priceBox (container do preço) SEMPRE que existir em qualquer um dos estilos
    if (baseStyle?.priceBox || styleToExport.priceBox) {
      const pbBase = baseStyle?.priceBox || {
        enabled: false,
        backgroundColor: '#ffffff',
        borderColor: '#000000',
        borderWidth: 0,
        borderRadius: 8,
        paddingTop: 4,
        paddingRight: 8,
        paddingBottom: 4,
        paddingLeft: 8,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
      };
      const pbExport = styleToExport.priceBox || {};
      completeStyle.priceBox = {
        ...pbBase,
        ...pbExport,
        // defaults seguros
        enabled: pbExport.enabled ?? pbBase.enabled ?? false,
        backgroundColor: pbExport.backgroundColor || pbBase.backgroundColor || '#ffffff',
        borderColor: pbExport.borderColor || pbBase.borderColor || '#000000',
        borderWidth: pbExport.borderWidth ?? pbBase.borderWidth ?? 0,
        borderRadius: pbExport.borderRadius ?? pbBase.borderRadius ?? 8,
        paddingTop: pbExport.paddingTop ?? pbBase.paddingTop ?? 4,
        paddingRight: pbExport.paddingRight ?? pbBase.paddingRight ?? 8,
        paddingBottom: pbExport.paddingBottom ?? pbBase.paddingBottom ?? 4,
        paddingLeft: pbExport.paddingLeft ?? pbBase.paddingLeft ?? 8,
        marginTop: pbExport.marginTop ?? pbBase.marginTop ?? 0,
        marginRight: pbExport.marginRight ?? pbBase.marginRight ?? 0,
        marginBottom: pbExport.marginBottom ?? pbBase.marginBottom ?? 0,
        marginLeft: pbExport.marginLeft ?? pbBase.marginLeft ?? 0,
      } as PriceBoxStyle;
    }

    // Incluir campos opcionais se existirem em qualquer um dos estilos
    if (baseStyle?.description || styleToExport.description) {
      const descBase = baseStyle?.description || { color: '#000000', fontSize: 16 };
      const descExport = styleToExport.description || { color: '#000000', fontSize: 16 };
      completeStyle.description = {
        ...descBase,
        ...descExport,
        // Garantir que campos obrigatórios sempre existam
        color: descExport.color || descBase.color || '#000000',
        fontSize: descExport.fontSize || descBase.fontSize || 16,
      };
    }

    if (baseStyle?.category || styleToExport.category) {
      const catBase = baseStyle?.category || { color: '#000000', fontSize: 16 };
      const catExport = styleToExport.category || { color: '#000000', fontSize: 16 };
      completeStyle.category = {
        ...catBase,
        ...catExport,
        // Garantir que campos obrigatórios sempre existam
        color: catExport.color || catBase.color || '#000000',
        fontSize: catExport.fontSize || catBase.fontSize || 16,
      };
    }

    if (baseStyle?.validUntil || styleToExport.validUntil) {
      const validBase = baseStyle?.validUntil || { color: '#000000', fontSize: 16 };
      const validExport = styleToExport.validUntil || { color: '#000000', fontSize: 16 };
      completeStyle.validUntil = {
        ...validBase,
        ...validExport,
        // Garantir que campos obrigatórios sempre existam
        color: validExport.color || validBase.color || '#000000',
        fontSize: validExport.fontSize || validBase.fontSize || 16,
      };
    }

    return completeStyle;
  };

  const handleImportStyle = (importedStyle: any) => {
    if (!selectedScreen || !importedStyle) return;

    if (selectedScreen.layoutId !== 'panfleto' && isPanfletoStyleExportFile(importedStyle)) {
      alert('Este arquivo é um estilo só para telas do tipo panfleto. Selecione uma tela panfleto para importar.');
      return;
    }

    if (selectedScreen.layoutId === 'panfleto' && isPanfletoStyleExportFile(importedStyle)) {
      const base =
        currentLayoutStyle ||
        selectedScreen.layoutStyle ||
        getDefaultLayoutStyleForLayoutId('panfleto');
      setCurrentLayoutStyle(mergePanfletoStyleImportIntoLayout(deepCopyLayoutStyle(base), importedStyle));
      setImportKey((prev) => prev + 1);
      return;
    }

    const isPanfletoImport =
      selectedScreen.layoutId === 'panfleto' &&
      importedStyle.panfletoContent &&
      typeof importedStyle.panfletoContent === 'object';

    if (
      !isPanfletoImport &&
      (!importedStyle.productName ||
        !importedStyle.price ||
        !importedStyle.unit ||
        !importedStyle.productContainer)
    ) {
      alert('O arquivo importado não contém um estilo de layout válido');
      return;
    }

    if (isPanfletoImport) {
      const base = getDefaultLayoutStyleForLayoutId('panfleto');
      setCurrentLayoutStyle({
        ...base,
        ...importedStyle,
        productName: importedStyle.productName || base.productName,
        price: importedStyle.price || base.price,
        unit: importedStyle.unit || base.unit,
        productContainer: importedStyle.productContainer || base.productContainer,
        panfletoContent: mergePanfletoContentPreserving(
          currentLayoutStyle?.panfletoContent || selectedScreen.layoutStyle?.panfletoContent,
          importedStyle.panfletoContent
        ),
      });
      setImportKey((prev) => prev + 1);
      return;
    }

    // Atualizar o estado local de edição (aparece no preview em tempo real)
    setCurrentLayoutStyle(importedStyle);

    // Forçar atualização do StyleEditor para que ele sincronize com os valores importados
    // Isso garante que todos os campos do editor sejam atualizados visualmente
    setImportKey(prev => prev + 1);
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="flex items-center mb-4 sm:mb-6">
        <button
          onClick={() => window.history.back()}
          className="mr-2 sm:mr-4 p-1.5 sm:p-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} className="sm:w-[20px] sm:h-[20px]" />
        </button>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Editor de Estilos</h2>
          <p className="text-gray-600 text-sm sm:text-base">Personalize as cores, fontes e espaçamentos dos elementos do layout</p>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg text-sm sm:text-base ${saveMessage.type === 'success'
          ? 'bg-green-50 text-green-700 border border-green-200'
          : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
          <p className="font-medium">{saveMessage.text}</p>
        </div>
      )}


      {/* Error State - Erro ao buscar telas da API */}
      {errorTelasAPI && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm sm:text-base">Erro ao carregar telas: {errorTelasAPI}</p>
        </div>
      )}

      {loadingScreen && (
        <div className="bg-blue-50 text-blue-700 p-4 sm:p-6 rounded-lg">
          <p className="text-center text-base sm:text-lg">Carregando configurações...</p>
        </div>
      )}

      {selectedScreen && !loadingScreen && (
        <>
          {/* Screen Selection e Botões - dropdown na esquerda, botões na direita */}
          <div className="mb-4 sm:mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Coluna Esquerda - Dropdown (mantido como estava) */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Selecione uma Tela
              </label>
              <select
                value={selectedScreenId || ''}
                onChange={(e) => setSelectedScreenId(Number(e.target.value))}
                className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-lg"
              >
                <option value="">Selecione uma tela...</option>
                {screens.map(screen => (
                  <option key={screen.id} value={screen.id}>
                    {screen.name} ({screen.layoutId})
                  </option>
                ))}
              </select>
            </div>
            {/* Coluna Direita - Botões alinhados com o preview */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
              <ImportExportButtons
                data={handleExportImportStyle()}
                onImport={handleImportStyle}
                fileName={
                  selectedScreen.layoutId === 'panfleto'
                    ? `estilo_panfleto_${selectedScreen.name.replace(/\s+/g, '_').toLowerCase()}.json`
                    : `estilo_${selectedScreen.name.replace(/\s+/g, '_').toLowerCase()}.json`
                }
                buttonTitle="Estilo"
              />
              <button
                onClick={handleSaveToAPI}
                disabled={saving}
                className="bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed h-[38px] sm:h-[42px] text-sm sm:text-base"
              >
                <Save size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>{saving ? 'Salvando...' : 'Salvar Estilo'}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Coluna Esquerda - Editor */}
            <div>
              {selectedScreen.layoutId === 'panfleto' ? (
                <PanfletoStyleEditor
                  key={`${selectedScreen.id}-${importKey}`}
                  screen={selectedScreen}
                  layoutStyle={currentLayoutStyle || selectedScreen.layoutStyle || getDefaultLayoutStyleForLayoutId('panfleto')}
                  onLayoutStyleChange={handleStyleChange}
                  onGetCurrentStyle={handleGetCurrentStyle}
                  forceSyncKey={importKey}
                  onPanfletoBackgroundUserChange={() => {
                    panfletoBgUserModifiedRef.current = true;
                  }}
                  panfletoFotoLoading={panfletoFotoLoading}
                />
              ) : (
                <StyleEditor
                  key={`${selectedScreen.id}-${importKey}`}
                  screen={{
                    ...selectedScreen,
                    layoutStyle: currentLayoutStyle || selectedScreen.layoutStyle || defaultLayoutStyle
                  }}
                  onStyleChange={handleStyleChange}
                  onGetCurrentStyle={handleGetCurrentStyle}
                  forceSyncKey={importKey}
                  numeroColunas={numeroColunas}
                />
              )}
            </div>

            {/* Coluna Direita - Preview */}
            <div className="lg:sticky lg:top-4 h-fit">
              {(() => {
                // Garantir que sempre temos um layoutStyle válido para o preview
                // Prioridade: currentLayoutStyle > selectedScreen.layoutStyle > defaultLayoutStyle
                const styleToUse = currentLayoutStyle || selectedScreen.layoutStyle || defaultLayoutStyle;

                // Se ainda não temos um layoutStyle válido, usar default baseado no layoutId
                const finalStyle = styleToUse || getDefaultLayoutStyleForLayoutId(selectedScreen.layoutId || 'simple2col');

                const panfletoHasProductsOnScreen = (() => {
                  const sid = selectedScreen.id;
                  const telaLista = telasOtimizadas.find((t) => t.id === sid);
                  if (telaLista && Array.isArray(telaLista.produtoIds)) {
                    return telaLista.produtoIds.length > 0;
                  }
                  return (
                    (selectedScreen.linkedProducts?.length ?? 0) > 0 ||
                    (selectedScreen.promotions?.length ?? 0) > 0
                  );
                })();

                return (
                  <StylePreview
                    promotion={previewPromotion}
                    layoutType={selectedScreen.layoutId || 'simple2col'}
                    layoutStyle={finalStyle}
                    panfletoHasProductsOnScreen={panfletoHasProductsOnScreen}
                    panfletoFotoLoading={panfletoFotoLoading}
                  />
                );
              })()}
            </div>
          </div>
        </>
      )}

      {!selectedScreen && !loadingScreen && (
        <>
          {/* Screen Selection quando não há tela selecionada */}
          <div className="mb-4 sm:mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Selecione uma tela para começar a editar seu estilo
              </label>
              <div className="relative flex items-center">
                <select
                  value={selectedScreenId || ''}
                  onChange={(e) => setSelectedScreenId(Number(e.target.value))}
                  className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-lg"
                >
                  <option value="">Selecione uma tela...</option>
                  {screens.map(screen => (
                    <option key={screen.id} value={screen.id}>
                      {screen.name} ({screen.layoutId})
                    </option>
                  ))}
                </select>
                {/* Loading animado ao lado do dropdown */}
                {loadingTelasAPI && (
                  <div className="absolute left-full ml-2 sm:ml-4 flex items-center gap-1.5 sm:gap-2">
                    <Loader size={16} className="animate-spin text-blue-500 sm:w-[18px] sm:h-[18px]" />
                    <p className="text-gray-600 text-xs sm:text-sm whitespace-nowrap">Carregando telas...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StyleEditorPage; 